import React, { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import IsNotMaintenanceMode from '../components/IsNotMaintenanceMode';
import Loader from '../components/Loader';
import NotificationsWrapper from '../components/NotificationsWrapper';
import { isGlobalVersionTwo } from '../helpers/Helpers';
import { IsAmbassador, IsExternalSpeaker, IsLoggedIn, IsProspect, IsWebviewProspects } from '../helpers/UserHelper';
import MainWrapper from './MainWrapper';
import RouteAction from './RouteAction';

const CarouselPage = lazy(() => import(/* webpackChunkName: "carousel" */ '../containers/Carousel'));
const CarouselStudentProfile = lazy(() => import(/* webpackChunkName: "carousel" */ '../containers/StudentProfile'));
const TapPageGroupChats = lazy(() => import(/* webpackChunkName: "chats" */ '../containers/GroupChats'));
const TapPageForum = lazy(() => import(/* webpackChunkName: "forum" */ '../containers/Forum'));
const TapPagePostsPage = lazy(() => import(/* webpackChunkName: "posts" */ '../containers/Posts'));
const PostPage = lazy(() => import(/* webpackChunkName: "post" */ '../containers/Post'));
const TapPageChatToUs = lazy(() => import(/* webpackChunkName: "ambassadors" */ '../containers/ChatToUs'));
const StudentProfile = lazy(() => import(/* webpackChunkName: "ambassador" */ '../containers/StudentProfile'));
const StudentProfileV2 = lazy(() => import(/* webpackChunkName: "tapV2" */ '../containers/StudentProfileV2'));
const FaqPage = lazy(() => import(/* webpackChunkName: "faq" */ '../containers/Faq'));
const TapPageFaqPage = lazy(() => import(/* webpackChunkName: "faqs" */ '../containers/Faqs'));
const Messaging = lazy(() => import(/* webpackChunkName: "messaging" */ '../containers/Messaging'));
const Settings = lazy(() => import(/* webpackChunkName: "settings" */ '../containers/Settings'));
const AmbassadorSettings = lazy(() => import(/* webpackChunkName: "sa-settings" */ '../containers/AmbassadorSettings'));
const SignIn = lazy(() => import(/* webpackChunkName: "login" */ '../containers/SignIn'));
const SignUp = lazy(() => import(/* webpackChunkName: "login" */ '../containers/SignUp'));
const AppStores = lazy(() => import(/* webpackChunkName: "login" */ '../containers/AppStores'));
const SignInStudent = lazy(() => import(/* webpackChunkName: "login-student" */ '../containers/SignInStudent'));
const SignInRedirect = lazy(() => import(/* webpackChunkName: "login-redirect" */ '../containers/SignInRedirect'));
const Feedback = lazy(() => import(/* webpackChunkName: "feedback" */ '../containers/Feedback'));
const SignInSuccess = lazy(() => import(/* webpackChunkName: "sso-sign-in-success" */ '../containers/SignInSuccess'));
const TapPageLiveStreams = lazy(() => import(/* webpackChunkName: "live-streams" */ '../containers/LiveStreams'));
const TapPageLiveStream = lazy(() => import(/* webpackChunkName: "live-event" */ '../containers/LiveStream'));
const RegisterAction = lazy(() => import(/* webpackChunkName: "register" */ './RegisterAction'));
const RegisterAutoAction = lazy(() => import(/* webpackChunkName: "register-auto" */ '../containers/RegisterAuto'));

const RegisterLayout = lazy(
    () => import(/* webpackChunkName: "ambassador-reg" */ '../containers/AmbassadorRegister/components/layout')
);
const AmbassadorRegister = lazy(
    () => import(/* webpackChunkName: "ambassador-reg" */ '../containers/AmbassadorRegister')
);
const AmbassadorProfiling = lazy(
    () => import(/* webpackChunkName: "ambassador-reg" */ '../containers/AmbassadorRegister/Profiling')
);

const renderMultipleRoutes = ({ element: element, paths, type = '', ...rest }) =>
    paths.map((path) => <Route key={path + '-' + type} path={path} {...rest} element={element} />);

const webviewProspectsRoutes = [
    renderMultipleRoutes({
        paths: [
            '/chat-to-us',
            '/carousel/chat-to-us',
            '/group/:university_id/chat-to-us',
            '/group/:university_id/carousel/chat-to-us',
            '/:university_id/chat-to-us',
            '/:university_id/carousel/chat-to-us',
            '/:university_id/:institutionSlug/chat-to-us',
            '/:university_id/:institutionSlug/carousel/chat-to-us',
        ],
        element: <TapPageChatToUs />,
    }),
    renderMultipleRoutes({
        paths: [
            '/interaction/settings',
            '/carousel/interaction/settings',
            '/group/:university_id/interaction/settings',
            '/:university_id/interaction/settings',
            '/group/:university_id/carousel/interaction/settings',
            '/:university_id/carousel/interaction/settings',
            '/:university_id/:institutionSlug/interaction/settings',
            '/:university_id/:institutionSlug/carousel/interaction/settings',
        ],
        element: <Settings />,
        type: 'prospect',
    }),
];

const commonRoutes = [
    renderMultipleRoutes({
        paths: [
            '/interaction/dialogs',
            '/carousel/interaction/dialogs',
            '/group/:university_id/interaction/dialogs',
            '/:university_id/interaction/dialogs',
            '/:university_id/:institutionSlug/interaction/dialogs',
            '/group/:university_id/carousel/interaction/dialogs',
            '/:university_id/carousel/interaction/dialogs',
            '/:university_id/:institutionSlug/carousel/interaction/dialogs',
            '/interaction/dialogs/:dialog_id',
            '/carousel/interaction/dialogs/:dialog_id',
            '/group/:university_id/carousel/interaction/dialogs/:dialog_id',
            '/group/:university_id/interaction/dialogs/:dialog_id',
            '/:university_id/interaction/dialogs/:dialog_id',
            '/:university_id/carousel/interaction/dialogs/:dialog_id',
            '/:university_id/:institutionSlug/interaction/dialogs/:dialog_id',
            '/:university_id/:institutionSlug/carousel/interaction/dialogs/:dialog_id',
        ],
        element: <Messaging />,
    }),
    renderMultipleRoutes({
        paths: [
            '/chat-to-us/:student',
            '/chat-to-us/university-data',
            '/group/:university_id/chat-to-us/university-data/:current_university',
            '/group/:university_id/chat-to-us/:student',
            '/:university_id/chat-to-us/:student',
            '/:university_id/chat-to-us/university-data',
            '/:university_id/:institutionSlug/chat-to-us/:student',
            '/:university_id/:institutionSlug/chat-to-us/university-data',
        ],
        element: isGlobalVersionTwo ? <StudentProfileV2 /> : <StudentProfile />,
    }),
    renderMultipleRoutes({
        paths: [
            '/carousel/chat-to-us/:student',
            '/carousel/chat-to-us/university-data',
            '/group/:university_id/carousel/chat-to-us/university-data/:current_university',
            '/group/:university_id/carousel/chat-to-us/:student',
            '/:university_id/carousel/chat-to-us/:student',
            '/:university_id/carousel/chat-to-us/university-data',
            '/:university_id/:institutionSlug/carousel/chat-to-us/:student',
            '/:university_id/:institutionSlug/carousel/chat-to-us/university-data',
        ],
        element: <CarouselStudentProfile />,
    }),
    renderMultipleRoutes({
        paths: [
            '/faq',
            '/carousel/faq',
            '/group/:university_id/faq',
            '/group/:university_id/carousel/faq',
            '/:university_id/faq',
            '/:university_id/carousel/faq',
            '/:university_id/:institutionSlug/faq',
            '/:university_id/:institutionSlug/carousel/faq',
        ],
        element: <TapPageFaqPage />,
    }),
    renderMultipleRoutes({
        paths: [
            '/faq/:faq',
            '/carousel/faq/:faq',
            '/group/:university_id/faq/:faq',
            '/group/:university_id/carousel/faq/:faq',
            '/:university_id/faq/:faq',
            '/:university_id/carousel/faq/:faq',
            '/:university_id/:institutionSlug/faq/:faq',
            '/:university_id/:institutionSlug/carousel/faq/:faq',
        ],
        element: <FaqPage />,
    }),
    renderMultipleRoutes({
        paths: [
            '/live-streams',
            '/carousel/live-streams',
            '/group/:university_id/live-streams',
            '/group/:university_id/carousel/live-streams',
            '/:university_id/live-streams',
            '/:university_id/carousel/live-streams',
            '/:university_id/:institutionSlug/live-streams',
            '/:university_id/:institutionSlug/carousel/live-streams',
        ],
        element: <TapPageLiveStreams testVersion={true} />,
    }),
    renderMultipleRoutes({
        paths: [
            '/group-chats',
            '/carousel/group-chats',
            '/group/:university_id/group-chats',
            '/group/:university_id/carousel/group-chats',
            '/:university_id/group-chats',
            '/:university_id/carousel/group-chats',
            '/:university_id/:institutionSlug/group-chats',
            '/:university_id/:institutionSlug/carousel/group-chats',
        ],
        element: <TapPageGroupChats testVersion={true} />,
    }),
    renderMultipleRoutes({
        paths: [
            '/forum',
            '/carousel/forum',
            '/group/:university_id/forum',
            '/group/:university_id/carousel/forum',
            '/:university_id/forum',
            '/:university_id/carousel/forum',
            '/:university_id/:institutionSlug/forum',
            '/:university_id/:institutionSlug/carousel/forum',
        ],
        element: <TapPageForum testVersion={true} />,
    }),
    renderMultipleRoutes({
        paths: [
            '/chat-to-us',
            '/carousel/chat-to-us',
            '/group/:university_id/chat-to-us',
            '/group/:university_id/carousel/chat-to-us',
            '/:university_id/chat-to-us',
            '/:university_id/carousel/chat-to-us',
            '/:university_id/:institutionSlug/chat-to-us',
            '/:university_id/:institutionSlug/carousel/chat-to-us',
        ],
        element: <TapPageChatToUs />,
    }),
    renderMultipleRoutes({
        paths: [
            '/feed',
            '/carousel/feed',
            '/group/:university_id/feed',
            '/group/:university_id/carousel/feed',
            '/:university_id/feed',
            '/:university_id/carousel/feed',
            '/posts',
            '/carousel/posts',
            '/group/:university_id/posts',
            '/group/:university_id/carousel/posts',
            '/:university_id/posts',
            '/:university_id/carousel/posts',
            '/:university_id/:institutionSlug/feed',
            '/:university_id/:institutionSlug/carousel/feed',
            '/:university_id/:institutionSlug/posts',
            '/:university_id/:institutionSlug/carousel/posts',
        ],
        element: <TapPagePostsPage />,
    }),
    renderMultipleRoutes({
        paths: [
            '/feed/:post',
            '/carousel/feed/:post',
            '/group/:university_id/feed/:post',
            '/group/:university_id/carousel/feed/:post',
            '/:university_id/feed/:post',
            '/:university_id/carousel/feed/:post',
            '/posts/:post',
            '/carousel/posts/:post',
            '/group/:university_id/posts/:post',
            '/group/:university_id/carousel/posts/:post',
            '/:university_id/posts/:post',
            '/:university_id/carousel/posts/:post',
            '/:university_id/:institutionSlug/feed/:post',
            '/:university_id/:institutionSlug/carousel/feed/:post',
            '/:university_id/:institutionSlug/posts/:post',
            '/:university_id/:institutionSlug/carousel/posts/:post',
        ],
        element: <PostPage />,
    }),
    renderMultipleRoutes({
        paths: [
            '/:university_id/interaction/sign-in-success',
            '/group/:university_id/interaction/sign-in-success',
            '/:university_id/:institutionSlug/interaction/sign-in-success',
        ],
        element: <SignInSuccess />,
    }),
    <Route key={'registerAuto'} path={'/:university_id/interaction/register/auto'} element={<RegisterAutoAction />} />,
    <Route key={'carousel1'} path={'/carousel'} element={<CarouselPage />} />,
    <Route key={'carousel2'} path={'/:university_id/carousel'} element={<CarouselPage />} />,
    <Route key={'carousel3'} path={'/:university_id/:institutionSlug/carousel'} element={<CarouselPage />} />,
];

const guestRoutes = [
    renderMultipleRoutes({
        paths: [
            '/interaction/:student_id',
            '/carousel/interaction/:student_id',
            '/group/:university_id/interaction/:student_id',
            '/group/:university_id/carousel/interaction/:student_id',
            '/:university_id/interaction/:student_id',
            '/:university_id/carousel/interaction/:student_id',
            '/:university_id/:institutionSlug/interaction/:student_id',
            '/:university_id/:institutionSlug/carousel/interaction/:student_id',
        ],
        element: <RegisterAction />,
        type: 'guest',
    }),
    renderMultipleRoutes({
        paths: ['/:university_id/interaction/register/auto'],
        element: <RegisterAutoAction />,
        type: 'guest',
    }),
    renderMultipleRoutes({
        paths: [
            '/interaction/sign-in',
            '/carousel/interaction/sign-in',
            '/group/:university_id/carousel/interaction/sign-in',
            '/group/:university_id/interaction/sign-in',
            '/:university_id/interaction/sign-in',
            '/:university_id/carousel/interaction/sign-in',
            '/:university_id/:institutionSlug/interaction/sign-in',
            '/:university_id/:institutionSlug/carousel/interaction/sign-in',
        ],
        element: <SignIn />,
        type: 'guest',
    }),
    renderMultipleRoutes({
        paths: [
            '/interaction/sign-up',
            '/carousel/interaction/sign-up',
            '/group/:university_id/carousel/interaction/sign-up',
            '/group/:university_id/interaction/sign-up',
            '/:university_id/interaction/sign-up',
            '/:university_id/carousel/interaction/sign-up',
            '/:university_id/:institutionSlug/interaction/sign-up',
            '/:university_id/:institutionSlug/carousel/interaction/sign-up',
        ],
        element: <SignUp />,
        type: 'guest',
    }),
    renderMultipleRoutes({
        paths: [
            '/interaction/feedback',
            '/carousel/interaction/feedback',
            '/group/:university_id/interaction/feedback',
            '/:university_id/interaction/feedback',
            '/group/:university_id/carousel/interaction/feedback',
            '/:university_id/carousel/interaction/feedback',
            '/:university_id/:institutionSlug/interaction/feedback',
            '/:university_id/:institutionSlug/carousel/interaction/feedback',
        ],
        element: <Feedback />,
        type: 'guest',
    }),

    <Route key={'app-stores'} path={'/interaction/app-stores'} element={<AppStores />} />,
    <Route key={'sign-in-guest'} path={'/interaction/ambassador/sign-in'} element={<SignInStudent />} />,
    <Route key={'sign-in-redirect-guest'} path={'/interaction/sign-in-redirect'} element={<SignInRedirect />} />,
    <Route key="sign-up" path="interaction/ambassador/sign-up" element={<RegisterLayout />}>
        <Route index element={<AmbassadorRegister />} />
        <Route path="profiling" element={<AmbassadorProfiling />} />
    </Route>,
];

const ambassadorRoutes = [
    renderMultipleRoutes({
        paths: [
            '/interaction/settings',
            '/carousel/interaction/settings',
            '/group/:university_id/interaction/settings',
            '/:university_id/interaction/settings',
            '/group/:university_id/carousel/interaction/settings',
            '/:university_id/carousel/interaction/settings',
            '/:university_id/:institutionSlug/interaction/settings',
            '/:university_id/:institutionSlug/carousel/interaction/settings',
        ],
        element: <AmbassadorSettings />,
        type: 'sa',
    }),
    renderMultipleRoutes({
        paths: [
            '/live-streams/:slug',
            '/carousel/live-streams/:slug',
            '/group/:university_id/live-streams/:slug',
            '/group/:university_id/carousel/live-streams/:slug',
            '/:university_id/live-streams/:slug',
            '/:university_id/carousel/live-streams/:slug',
            '/:university_id/:institutionSlug/live-streams/:slug',
            '/:university_id/:institutionSlug/carousel/live-streams/:slug',
        ],
        element: <TapPageLiveStream />,
        type: 'sa',
    }),
    <Route
        key="profiling"
        path="/:university_id/interaction/ambassador/profiling"
        element={<RegisterLayout isLoggedIn />}
    >
        <Route index element={<AmbassadorProfiling />} />,
    </Route>,
];

const prospectRoutes = [
    renderMultipleRoutes({
        paths: [
            '/interaction/settings',
            '/carousel/interaction/settings',
            '/group/:university_id/interaction/settings',
            '/:university_id/interaction/settings',
            '/group/:university_id/carousel/interaction/settings',
            '/:university_id/carousel/interaction/settings',
            '/:university_id/:institutionSlug/interaction/settings',
            '/:university_id/:institutionSlug/carousel/interaction/settings',
        ],
        element: <Settings />,
        type: 'prospect',
    }),
    renderMultipleRoutes({
        paths: [
            '/interaction/feedback',
            '/carousel/interaction/feedback',
            '/group/:university_id/interaction/feedback',
            '/:university_id/interaction/feedback',
            '/group/:university_id/carousel/interaction/feedback',
            '/:university_id/carousel/interaction/feedback',
            '/:university_id/:institutionSlug/interaction/feedback',
            '/:university_id/:institutionSlug/carousel/interaction/feedback',
        ],
        element: <Feedback />,
        type: 'prospect',
    }),
    renderMultipleRoutes({
        paths: [
            '/interaction/:student_id',
            '/carousel/interaction/:student_id',
            '/group/:university_id/interaction/:student_id',
            '/:university_id/interaction/:student_id',
            '/group/:university_id/carousel/interaction/:student_id',
            '/:university_id/carousel/interaction/:student_id',
            '/:university_id/:institutionSlug/interaction/:student_id',
            '/:university_id/:institutionSlug/carousel/interaction/:student_id',
        ],
        element: <RegisterAction />,
        type: 'prospect',
    }),
    renderMultipleRoutes({
        paths: [
            '/live-streams/:slug',
            '/carousel/live-streams/:slug',
            '/group/:university_id/live-streams/:slug',
            '/group/:university_id/carousel/live-streams/:slug',
            '/:university_id/live-streams/:slug',
            '/:university_id/carousel/live-streams/:slug',
            '/:university_id/:institutionSlug/live-streams/:slug',
            '/:university_id/:institutionSlug/carousel/live-streams/:slug',
        ],
        element: <TapPageLiveStream />,
        type: 'prospect',
    }),
];

const externalSpeakerRoutes = [
    renderMultipleRoutes({
        paths: [
            '/live-streams/:slug',
            '/carousel/live-streams/:slug',
            '/group/:university_id/live-streams/:slug',
            '/group/:university_id/carousel/live-streams/:slug',
            '/:university_id/live-streams/:slug',
            '/:university_id/carousel/live-streams/:slug',
            '/:university_id/:institutionSlug/live-streams/:slug',
            '/:university_id/:institutionSlug/carousel/live-streams/:slug',
        ],
        element: <TapPageLiveStream />,
        type: 'speaker',
    }),
];

const Router = () => (
    <MainWrapper>
        <main>
            <Suspense fallback={<Loader />}>
                <NotificationsWrapper>
                    <IsNotMaintenanceMode>
                        <Routes>
                            {!IsWebviewProspects() && commonRoutes}
                            {!IsLoggedIn() && guestRoutes}
                            {IsProspect() && !IsWebviewProspects() && prospectRoutes}
                            {IsWebviewProspects() && webviewProspectsRoutes}
                            {IsAmbassador() && ambassadorRoutes}
                            {IsExternalSpeaker() && externalSpeakerRoutes}
                            <Route path="/*" element={<RouteAction testVersion={true} />} />
                            <Route path="/:university_id/*" element={<RouteAction testVersion={true} />} />
                        </Routes>
                    </IsNotMaintenanceMode>
                </NotificationsWrapper>
            </Suspense>
        </main>
    </MainWrapper>
);

export default Router;
